import * as React from "react";

import * as H from "history";
import { useHistory, useLocation } from "react-router-dom";

import { IDatasource } from "@generation/core";

import { EditForm } from "./EditForm";
import { ReadOnlyForm } from "./ReadOnlyForm";
import { createPageFlowContext, PageFlowContextProvider } from "../Context/page-flow";
import { IPageFlowStep } from "../Interfaces/page-flow";
import { translate } from "../Locales/translator";
import { recursivelyFlatten, shallowFlatten } from "../Utils/flatten";

type LocationType<T> = {
  values: T;
  stepName: string;
  sessionId: number;
};

const updateUrlInBrowser = <T extends unknown>(
  formStepUrlPath: string,
  stepName: string,
  history: H.History<unknown>,
  location: H.Location<LocationType<T>>,
  values: T | undefined,
  sessionId: number
) => {
  if (!location.state || stepName !== location.state.stepName) {
    history.push(`${formStepUrlPath}`, {
      ...location.state,
      values,
      stepName,
      sessionId
    });
  }
};

export enum FormState {
  Flow = "flow",
  Result = "result"
}

export const OnStepChangeScrollToHashPrefix = "sectionName";

export interface IGenericRekenmoduleProps {
  rekenmoduleTitle?: string;
  readOnlyFormIntroText?: string;
  finalFlowCategoryTitle: string;
  showTitleOnResultPage?: boolean;
  startFlowStep: IPageFlowStep;
  pageBaseUrl: string;
  apiBaseUrl: string;
  datasources?: IDatasource[];
  variables: any;
  defaultValues?: any;
  flowSteps: IPageFlowStep[];
  availableCategories: string[];
  moduleUrlPart: string;
  renderStepCategoryIndicator: (flowCategories: string[], showAllAsFinished: boolean) => JSX.Element;
  renderResultPage: (
    values: { [key: string]: unknown },
    onStartOver: () => void,
    submitResult?: unknown
  ) => JSX.Element;
}

export const GenericRekenmodule = (props: IGenericRekenmoduleProps) => {
  const history = useHistory();
  const location = useLocation<LocationType<any>>();
  const { hash } = useLocation();
  const [formState, setFormState] = React.useState<FormState>(FormState.Flow);
  const [values, setValues] = React.useState<{
    [key: string]: unknown;
  }>({});

  const pageFlowContext = createPageFlowContext(props.startFlowStep);
  const {
    currentFlowStep,
    setCurrentFlowStep,
    flowStepHistory,
    setFlowStepHistory,
    currentFlowStepRef,
    flowStepHistoryRef
  } = pageFlowContext;

  const [additionalData, setAdditionalData] = React.useState<{ [key: string]: unknown }>({});

  const [flowCategories, setFlowCategories] = React.useState<string[]>([props.startFlowStep.categoryName]);
  const [error, setError] = React.useState("");
  const [sessionId, _setSessionId] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [submitResult, setSubmitResult] = React.useState<unknown>();

  const updateUrl = (formStepUrlPath: string, flowStep: IPageFlowStep) => {
    updateUrlInBrowser<any>(formStepUrlPath, currentFlowStep.name, history, location, values, sessionId);
  };

  React.useEffect(() => {
    const hashPrefixValue = `#${OnStepChangeScrollToHashPrefix}=`;
    const hashPrefixIndex = hash.indexOf(hashPrefixValue);
    const sectionElement =
      hashPrefixIndex >= 0 ? document.querySelector(`form .${hash.replace(hashPrefixValue, "")}`) : null;

    currentFlowStepRef.current = currentFlowStep;

    if (sectionElement) {
      sectionElement.scrollIntoView();
    } else if (window && window.scrollTo) {
      window.scrollTo({
        top: 0
      });
    }
  }, [currentFlowStep]);

  React.useEffect(() => {
    flowStepHistoryRef.current = flowStepHistory;
  }, [flowStepHistory]);

  React.useEffect(() => {
    if (`${props.pageBaseUrl}/${props.moduleUrlPart}/${currentFlowStep.name}` === location.pathname) {
      return;
    }
    const flowStep = props.flowSteps.find(
      (flowStep) => `${props.pageBaseUrl}/${props.moduleUrlPart}/${flowStep.name}` === location.pathname
    );
    if (flowStep) {
      if (
        flowStepHistoryRef.current.length > 1 ||
        (flowStepHistoryRef.current.length === 1 && Object.keys(values).length > 0)
      ) {
        const currentItemInHistoryIndex = flowStepHistoryRef.current.findIndex(
          (flowStepInHistory) => flowStepInHistory.name === flowStep.name
        );
        if (currentItemInHistoryIndex >= 0) {
          setFlowStepHistory([...flowStepHistoryRef.current.slice(0, currentItemInHistoryIndex), flowStep]);
        }

        const index = props.availableCategories.findIndex((category) => category === flowStep.categoryName);
        if (index >= 0) {
          setFlowCategories([...props.availableCategories.slice(0, index + 1)]);
        }

        setCurrentFlowStep(flowStep);
      } else {
        if (location.pathname !== `${props.pageBaseUrl}/${props.moduleUrlPart}/${props.startFlowStep.name}`) {
          if (formState === FormState.Result) {
            setFormState(FormState.Flow);
            setFlowStepHistory([props.startFlowStep]);
          }

          setCurrentFlowStep(props.startFlowStep);
          setFlowCategories([props.startFlowStep.categoryName]);
          history.replace(`${props.pageBaseUrl}/${props.moduleUrlPart}/${props.startFlowStep.name}`);
        }
      }
    } else {
      if (location.pathname !== `${props.pageBaseUrl}/${props.moduleUrlPart}/${props.startFlowStep.name}`) {
        setCurrentFlowStep(props.startFlowStep);
        setFlowCategories([props.startFlowStep.categoryName]);
        history.replace(`${props.pageBaseUrl}/${props.moduleUrlPart}/${props.startFlowStep.name}`);
      }
    }
  }, [location]);

  const handleStoreValues = (formValues: unknown) => {
    const oldValues = values[currentFlowStep.name] || {};

    const flattenedOldValues = (recursivelyFlatten(oldValues as {}) as {}) || {};
    const flattenedNewValues = (recursivelyFlatten(formValues as {}) as {}) || {};

    const flattenedOldValueKeys = Object.keys(flattenedOldValues) || [];
    const flattenedNewValueKeys = Object.keys(flattenedNewValues) || [];

    const updatedValues = { ...values };
    updatedValues[currentFlowStep.name] = formValues as {};

    const compareValues = () => {
      let result = true;

      const compareByKey = (keyName) => {
        if (flattenedNewValues[keyName] !== flattenedOldValues[keyName]) {
          result = false;
        }
      };

      flattenedOldValueKeys.forEach(compareByKey);
      flattenedNewValueKeys.forEach(compareByKey);

      return result;
    };

    if (
      currentFlowStep.clearStepsAfterChange &&
      (flattenedOldValueKeys.length !== flattenedNewValueKeys.length || !compareValues())
    ) {
      currentFlowStep.clearStepsAfterChange.forEach((keyName) => {
        if (values[keyName.name]) {
          delete updatedValues[keyName.name];
        }
      });
    }

    setValues(updatedValues);

    return updatedValues;
  };

  const handleNext = async (updatedValues: { [x: string]: unknown }): Promise<boolean> => {
    if (currentFlowStep.getNextFlowStep || currentFlowStep.nextFlowStep) {
      if (currentFlowStep.getDataOnSubmit) {
        try {
          setIsLoading(true);
          setError("");
          const data = await currentFlowStep.getDataOnSubmit(props.apiBaseUrl, shallowFlatten(updatedValues));
          updatedValues = {
            ...updatedValues,
            ...additionalData,
            [`${currentFlowStep.name}_data`]: { ...data }
          };
          setAdditionalData({ ...additionalData, [`${currentFlowStep.name}_data`]: { ...data } });
          setIsLoading(false);
        } catch (err) {
          setError(
            currentFlowStep.getErrorTextOnGetDataOnSubmitError || translate("ErrorTextOnGetDataOnSubmitError")
          );
          setIsLoading(false);
          return false;
        }
      }
      const currentCategory = flowCategories.length > 0 ? flowCategories[flowCategories.length - 1] : "";

      if (currentFlowStep.getNextFlowStep) {
        setIsLoading(true);
        currentFlowStep
          .getNextFlowStep(shallowFlatten(updatedValues))
          .then((nextFlow) => {
            setIsLoading(false);
            if (nextFlow) {
              showNextFlow(nextFlow, currentCategory);
            } else {
              setFlowCategories([...flowCategories, props.finalFlowCategoryTitle]);
              setFormState(FormState.Result);
            }
          })
          .catch(() => {
            setIsLoading(false);
          });
      } else if (currentFlowStep.nextFlowStep) {
        showNextFlow(currentFlowStep.nextFlowStep, currentCategory);
      }
    } else {
      setFlowCategories([...flowCategories, props.finalFlowCategoryTitle]);
      setFormState(FormState.Result);
    }

    return true;
  };

  const storeValuesAndHandleNext = async (formValues: unknown) => {
    const updatedValues = handleStoreValues(formValues);
    return handleNext(updatedValues);
  };

  const handleSubmit = (options: { shouldStoreValues: boolean }) => async (formValues: unknown) => {
    if (currentFlowStep.onCustomValidateOnSubmit && !currentFlowStep.onCustomValidateOnSubmit(formValues)) {
      return false;
    }
    if (currentFlowStep.onSubmit) {
      setIsLoading(true);
      const allData = {
        ...props.variables,
        ...(shallowFlatten(additionalData) as {}),
        ...(shallowFlatten(values) as {})
      };
      currentFlowStep.onSubmit(allData).then(
        (result: unknown) => {
          setSubmitResult(result);
          if (result) {
            return options.shouldStoreValues
              ? storeValuesAndHandleNext(formValues)
              : handleNext(formValues as {});
          }
          setError(currentFlowStep.errorTextOnSubmit || translate("ErrorTextOnGetDataOnSubmitError"));
          setIsLoading(false);
          return false;
        },
        () => {
          setIsLoading(false);
          setSubmitResult(undefined);
          setError(currentFlowStep.errorTextOnSubmit || translate("ErrorTextOnGetDataOnSubmitError"));
        }
      );
      return;
    }

    return options.shouldStoreValues ? storeValuesAndHandleNext(formValues) : handleNext(formValues as {});
  };

  const showNextFlow = React.useCallback(
    (nextFlow: IPageFlowStep, currentCategory: string) => {
      setError("");
      if (nextFlow) {
        setFlowStepHistory([...flowStepHistory, nextFlow]);
        const index = props.availableCategories.findIndex((category) => category === nextFlow.categoryName);
        if (index >= 0) {
          setFlowCategories([...props.availableCategories.slice(0, index + 1)]);
        }
        setCurrentFlowStep(nextFlow);
        updateUrl(`${props.pageBaseUrl}/${props.moduleUrlPart}/${nextFlow.name}`, nextFlow);
      }
    },
    [flowStepHistory, flowCategories, props.availableCategories]
  );

  const handlePreviousAndSaveValuesIfGiven = (formValues: unknown) => {
    if (formValues && Object.keys(formValues).length > 0) {
      handleStoreValues(formValues);
    }

    handlePrevious();
  };

  const handlePrevious = () => {
    setError("");
    if (flowStepHistory.length > 0) {
      const currentItemInHistoryIndex = flowStepHistory.findIndex(
        (flowStepInHistory) => flowStepInHistory.name === currentFlowStep.name
      );
      if (currentItemInHistoryIndex > 0) {
        const previousFlowStep = flowStepHistory[currentItemInHistoryIndex - 1];
        const index = props.availableCategories.findIndex(
          (category) => category === previousFlowStep.categoryName
        );
        if (index >= 0) {
          setFlowCategories([...props.availableCategories.slice(0, index + 1)]);
        }
        setCurrentFlowStep(previousFlowStep);
        setFlowStepHistory(flowStepHistory.slice(0, currentItemInHistoryIndex));
        updateUrl(`${props.pageBaseUrl}/${props.moduleUrlPart}/${previousFlowStep.name}`, previousFlowStep);
      }
    }
  };

  const resetForm = () => {
    setValues({});
    setAdditionalData({});
    setFormState(FormState.Result);
    setFlowStepHistory([]);
  };

  if (formState === FormState.Result || currentFlowStep.isResultStep) {
    const onStartOver = () => {
      setValues({});
      setAdditionalData({});
      setCurrentFlowStep(props.startFlowStep);
      setFlowStepHistory([props.startFlowStep]);
      setFlowCategories([props.startFlowStep.categoryName]);
      setFormState(FormState.Flow);
      updateUrl(
        `${props.pageBaseUrl}/${props.moduleUrlPart}/${props.startFlowStep.name}`,
        props.startFlowStep
      );
    };

    const output = {
      ...props.variables,
      ...(shallowFlatten(additionalData) as {}),
      ...(shallowFlatten(values) as {})
    };

    const resultPage =
      currentFlowStep.renderResultPage?.(output as { [key: string]: unknown }, onStartOver, submitResult) ??
      props.renderResultPage(output as { [key: string]: unknown }, onStartOver, submitResult);

    return (
      <PageFlowContextProvider value={{ ...pageFlowContext, handleSubmit, handlePrevious, resetForm }}>
        {props.showTitleOnResultPage && props.rekenmoduleTitle && (
          <h1 className="form-title form-title__reken-module">{props.rekenmoduleTitle}</h1>
        )}
        {props.renderStepCategoryIndicator(flowCategories, true)}
        {resultPage}
      </PageFlowContextProvider>
    );
  }

  const allData = {
    ...props.variables,
    ...(shallowFlatten(additionalData) as {}),
    ...(shallowFlatten(values) as {})
  };

  /* .. die key bij BaseForm is nodig om de rerender van elke flow-stap te forceren..
          anders houdt het formulier de values van de vorige stap vast */
  return (
    <PageFlowContextProvider value={{ ...pageFlowContext, handleSubmit, handlePrevious, resetForm }}>
      {props.rekenmoduleTitle && (
        <h1 className="form-title form-title__reken-module">{props.rekenmoduleTitle}</h1>
      )}
      {props.renderStepCategoryIndicator(flowCategories, !!currentFlowStep.showAllStepsAsVisited)}
      {currentFlowStep.isReadOnly ? (
        <ReadOnlyForm
          key={currentFlowStep.name}
          stepName={currentFlowStep.name}
          introText={
            currentFlowStep.introTextTranslate
              ? currentFlowStep.introTextTranslate.translator.translate(
                  currentFlowStep.introTextTranslate.translateKey,
                  allData
                )
              : currentFlowStep.introText || props.readOnlyFormIntroText
          }
          outroText={currentFlowStep.outroText}
          datasources={props.datasources}
          getSchema={currentFlowStep.getSchema(allData)}
          values={currentFlowStep.transformData ? currentFlowStep.transformData(allData) : allData}
          handleSubmit={handleSubmit({ shouldStoreValues: false })}
          hasGotoPreviousButton={flowStepHistory.length > 1}
          handlePrevious={handlePrevious}
          isSubmitting={isLoading}
          hasSubmitButton={
            !!(currentFlowStep.nextFlowStep || currentFlowStep.getNextFlowStep || currentFlowStep.onSubmit)
          }
          customClass={currentFlowStep.customClass}
          volgendeButtonLabel={currentFlowStep.resultStepNextBtnLabel}
        />
      ) : (
        <EditForm
          key={currentFlowStep.name}
          introText={
            currentFlowStep.introTextTranslate
              ? currentFlowStep.introTextTranslate.translator.translate(
                  currentFlowStep.introTextTranslate.translateKey,
                  allData
                )
              : currentFlowStep.introText || props.readOnlyFormIntroText
          }
          outroText={currentFlowStep.outroText}
          getSchema={currentFlowStep.getSchema(allData)}
          handleSubmit={handleSubmit({ shouldStoreValues: true })}
          handlePrevious={flowStepHistory.length > 1 ? handlePreviousAndSaveValuesIfGiven : undefined}
          defaultValues={
            currentFlowStep.onGetDefaultValues
              ? currentFlowStep.onGetDefaultValues(allData)
              : values[currentFlowStep.name] || props.defaultValues?.[currentFlowStep.name] || {}
          }
          datasources={props.datasources}
          customClass={currentFlowStep.customClass}
          isLoading={isLoading}
        />
      )}
      {error && (
        <div className="alert alert-danger error-text" role="alert" aria-live="polite">
          {error}
        </div>
      )}
    </PageFlowContextProvider>
  );
};
